import { FunctionCallCache } from '../helpers/functionCallCache';
import axios from 'axios';
import { runFullCleanup } from '../helpers/functionCleanup';
import { FunctionLocationTracker } from '../helpers/functionLocationTracker';
import { log } from './logger';
import { HttpClient } from '../helpers/httpClient';
async function fetchFromAlphaScriptApi(batch) {
    const CHUNK_SIZE = 5000;
    const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    };
    const chunks = chunkArray(batch, CHUNK_SIZE);
    const results = [];
    for (const chunk of chunks) {
        try {
            const response = await HttpClient.post('/excel/alpha-function', chunk);
            results.push(...response.data);
        }
        catch (error) {
            log.error('Error fetching from AlphaScript:', error);
            if (axios.isAxiosError(error)) {
                log.error('Axios Error Message:', error.message);
                log.error('Axios Error Code:', error.code);
                log.error('Axios Error Config:', error.config);
                if (error.response) {
                    log.error('Server responded with status:', error.response.status);
                    log.error('Response data:', error.response.data);
                }
                else if (error.request) {
                    log.error('No response received:', error.request);
                }
            }
            else {
                log.error('Non-Axios error:', error);
            }
            results.push(...chunk.map((state) => ({
                key: state.key,
                data: null,
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred',
            })));
        }
    }
    return results;
}
// Sentinel sheet is used notify when the aphafunction have recalculated in the render plugin
export async function createSentinelSheet() {
    await Excel.run(async (context) => {
        const workbook = context.workbook;
        workbook.worksheets.add('AlphascriptFunctionsReady');
        await context.sync();
    });
}
const SENTINEL_DELAY_MS = 10_000;
let sentinelTimeout = null;
let pendingBatchCount = 0;
export function cancelSentinelSheet() {
    if (sentinelTimeout)
        clearTimeout(sentinelTimeout);
    sentinelTimeout = null;
}
export function incrementPendingBatch() {
    pendingBatchCount++;
    cancelSentinelSheet();
}
export function decrementPendingBatch() {
    pendingBatchCount = Math.max(0, pendingBatchCount - 1);
    if (pendingBatchCount === 0) {
        scheduleSentinelSheet();
    }
}
function isWorkbookReady() {
    const cache = FunctionCallCache.getInstance();
    if (cache.cache.size === 0)
        return false;
    for (const entry of cache.cache.values()) {
        if (entry.status !== 'ready' && entry.status !== 'error')
            return false;
    }
    return true;
}
export function scheduleSentinelSheet() {
    if (!FunctionCallCache.RENDER_PLUGIN_MODE)
        return;
    if (pendingBatchCount > 0) {
        log.warn(`Skipped - ${pendingBatchCount} batches pending`);
        return;
    }
    cancelSentinelSheet();
    sentinelTimeout = setTimeout(() => {
        sentinelTimeout = null;
        if (pendingBatchCount > 0 || !isWorkbookReady()) {
            FunctionCallCache.setAutoRefreshUntil();
            scheduleSentinelSheet();
            return;
        }
        void createSentinelSheet().catch((e) => log.error('Error creating sentinel sheet', e));
    }, SENTINEL_DELAY_MS);
}
/**
 * Forces a **full workbook recalculation** and resolves only after Excel reports
 * that calculation is **done**.
 *
 * How it works:
 * - Subscribes to `worksheets.onCalculated`.
 * - Triggers `application.calculate(Excel.CalculationType.full)`.
 * - Polls `application.calculationState`; when it equals `done`, unsubscribes
 *   the handler and resolves.
 *
 * Returns:
 * - A promise that resolves when calculation completes, or rejects on error.
 *
 * Notes:
 * - The event is removed regardless of success to avoid leaking handlers.
 */
async function recalculateAndWait() {
    return new Promise((resolve, reject) => {
        let handled = false;
        let processing = false;
        Excel.run(async (ctx) => {
            const handler = async () => {
                if (handled || processing)
                    return;
                processing = true;
                try {
                    await Excel.run(async (innerCtx) => {
                        innerCtx.application.load('calculationState');
                        await innerCtx.sync();
                        const done = innerCtx.application.calculationState === Excel.CalculationState.done;
                        if (done) {
                            handled = true;
                            innerCtx.workbook.worksheets.onCalculated.remove(handler);
                            await innerCtx.sync();
                            resolve();
                        }
                    });
                }
                catch (e) {
                    reject(e);
                }
                finally {
                    processing = false;
                }
            };
            ctx.workbook.worksheets.onCalculated.add(handler);
            await ctx.sync();
            ctx.workbook.application.calculate(Excel.CalculationType.full);
            await ctx.sync();
        }).catch(reject);
    });
}
/**
 * Recalculates only the cells that correspond to the provided **function keys**,
 * doing so in **chunks** per worksheet to reduce round-trips and throttle load.
 *
 * Strategy:
 * 1) Look up all tracked cell locations for the given keys via
 *    `FunctionLocationTracker` and group addresses by `sheetId`.
 * 2) For each sheet, batch addresses into comma-separated ranges (CHUNK_SIZE=500)
 *    and call `getRanges(...).calculate()` for each chunk.
 * 3) After every ~SYNC_THRESHOLD addresses (2000), `ctx.sync()` to flush work and
 *    extend the auto-refresh window (`FunctionCallCache.setAutoRefreshUntil()`).
 *
 * Parameters:
 * - `functionKeys`: array of cache keys to recalc (duplicates are deduped).
 *
 * Behavior:
 * - Skips mappings without a known `sheetId`.
 * - Logs a warning and skips a chunk if a bulk calculate fails on that sheet.
 * - No-ops if none of the keys are currently tracked in the workbook.
 */
async function recalculateForFunctionKeysInChunks(functionKeys) {
    const CHUNK_SIZE = 500;
    const SYNC_THRESHOLD = 2000;
    const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    };
    const tracker = await FunctionLocationTracker.getInstance();
    const keySet = new Set(functionKeys);
    const mappings = tracker.getAllMappings().filter((m) => keySet.has(m.key));
    if (mappings.length === 0)
        return;
    const sheetMap = {};
    for (const mapping of mappings) {
        if (!mapping.sheetId)
            continue;
        if (!sheetMap[mapping.sheetId]) {
            sheetMap[mapping.sheetId] = [];
        }
        sheetMap[mapping.sheetId].push(mapping.address);
    }
    await Excel.run(async (ctx) => {
        let pendingAddresses = 0;
        for (const [sheetId, addresses] of Object.entries(sheetMap)) {
            const ws = ctx.workbook.worksheets.getItem(sheetId);
            const chunks = chunkArray(addresses, CHUNK_SIZE);
            for (const chunk of chunks) {
                try {
                    // Bulk calculate the function ranges
                    const addressString = chunk.join(', ');
                    const ranges = ws.getRanges(addressString);
                    ranges.calculate();
                    pendingAddresses += chunk.length;
                    // Sync if needed
                    if (pendingAddresses >= SYNC_THRESHOLD) {
                        await ctx.sync();
                        FunctionCallCache.setAutoRefreshUntil();
                        pendingAddresses = 0;
                    }
                }
                catch (err) {
                    log.warn(`Chunk failed on sheet ID "${sheetId}", skipping ${chunk.length} ranges`, err);
                }
            }
        }
        await ctx.sync();
        FunctionCallCache.setAutoRefreshUntil();
    });
}
export async function recalculateForFunctionKeys(functionKeys) {
    if (FunctionCallCache.RENDER_PLUGIN_MODE) {
        await recalculateAndWait();
        FunctionCallCache.setAutoRefreshUntil();
        scheduleSentinelSheet();
    }
    else {
        await recalculateForFunctionKeysInChunks(functionKeys);
    }
}
export async function refreshWorkbookAlphaLinks() {
    try {
        await runFullCleanup();
        FunctionCallCache.setAutoRefreshArea({ type: 'Workbook' });
        const cache = FunctionCallCache.getInstance();
        const queries = await cache.getPendingApiCallsForWorkbook();
        const results = await fetchFromAlphaScriptApi(queries);
        cache.applyApiResults(results);
        FunctionCallCache.setAutoRefreshUntil();
        await recalculateForFunctionKeys(results.map((result) => result.key));
    }
    catch (error) {
        log.error('Error refreshing workbook alpha links', error);
    }
}
export async function refreshSelectedCellsAlphaLinks() {
    try {
        await runFullCleanup();
        const { selectedAddresses, worksheetId, worksheetName, rangeAddress } = await Excel.run(async (ctx) => {
            const sel = ctx.workbook.getSelectedRange();
            sel.load(['worksheet/id', 'worksheet/name', 'address']);
            await ctx.sync();
            const formulaAreas = sel.getSpecialCellsOrNullObject(Excel.SpecialCellType.formulas);
            formulaAreas.load('isNullObject');
            await ctx.sync();
            if (formulaAreas.isNullObject) {
                return {
                    selectedAddresses: [],
                    worksheetId: sel.worksheet.id,
                    worksheetName: sel.worksheet.name,
                    rangeAddress: sel.address,
                };
            }
            // We need bounds to expand blocks into single cells
            formulaAreas.areas.load('items/rowIndex,items/columnIndex,items/rowCount,items/columnCount');
            await ctx.sync();
            // Helpers
            const toCol = (n) => {
                let s = '';
                n++;
                while (n > 0) {
                    const m = (n - 1) % 26;
                    s = String.fromCharCode(65 + m) + s;
                    n = Math.floor((n - 1) / 26);
                }
                return s;
            };
            const quoteSheetName = (name) => (/[^A-Za-z0-9_]/.test(name) ? `'${name.replace(/'/g, "''")}'` : name);
            const sheetPrefix = `${quoteSheetName(sel.worksheet.name)}!`;
            const cells = [];
            for (const a of formulaAreas.areas.items) {
                for (let r = 0; r < a.rowCount; r++) {
                    for (let c = 0; c < a.columnCount; c++) {
                        const addr = `${toCol(a.columnIndex + c)}${a.rowIndex + r + 1}`;
                        cells.push(sheetPrefix + addr);
                    }
                }
            }
            return {
                selectedAddresses: cells,
                worksheetId: sel.worksheet.id,
                worksheetName: sel.worksheet.name,
                rangeAddress: sel.address, // already properly quoted by Excel
            };
        });
        FunctionCallCache.setAutoRefreshArea({
            type: 'Range',
            worksheetId,
            worksheetName,
            rangeAddress,
        });
        const cache = FunctionCallCache.getInstance();
        const allQueries = [];
        for (const address of selectedAddresses) {
            const queries = await cache.getPendingApiCallsForCell(address);
            allQueries.push(...queries);
        }
        if (allQueries.length === 0) {
            return;
        }
        const results = await fetchFromAlphaScriptApi(allQueries);
        cache.applyApiResults(results);
        const keysToRecalculate = Array.from(new Set(results.map((r) => r.key))); // Remove duplicates
        await recalculateForFunctionKeys(keysToRecalculate);
    }
    catch (error) {
        log.error('Error refreshing selected cells alpha links', error);
    }
}
export async function refreshWorksheetAlphaLinks() {
    try {
        await runFullCleanup();
        const { id: worksheetId, name: worksheetName } = await Excel.run(async (context) => {
            const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
            activeWorksheet.load(['id', 'name']);
            await context.sync();
            return { id: activeWorksheet.id, name: activeWorksheet.name };
        });
        FunctionCallCache.setAutoRefreshArea({ type: 'Worksheet', worksheetId, worksheetName });
        const cache = FunctionCallCache.getInstance();
        const queries = await cache.getPendingApiCallsForSheetId(worksheetId);
        const results = await fetchFromAlphaScriptApi(queries);
        cache.applyApiResults(results);
        await recalculateForFunctionKeys(results.map((result) => result.key));
    }
    catch (error) {
        log.error('Error refreshing worksheet alpha links', error);
    }
}
export async function refreshAllAutoRefreshAlphaLinks(keys) {
    try {
        await runFullCleanup();
        const cache = FunctionCallCache.getInstance();
        const queries = cache._buildApiCallListFromKeys(new Set(keys));
        const results = await fetchFromAlphaScriptApi(queries);
        cache.applyApiResults(results);
        FunctionCallCache.setAutoRefreshUntil();
        await recalculateForFunctionKeys(results.map((result) => result.key));
    }
    catch (error) {
        log.error('Error refreshing all auto refresh alpha links', error);
    }
}
//# sourceMappingURL=alphaFunctions.js.map